import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Input } from '../ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { FileText, Download, RefreshCw, Plus, Search, Filter } from '../ui/icons';
import { usePermissions } from '../../hooks/usePermissions';
import { documentsApi } from '../../services/api';
import { Document } from '../../services/api';

const MyDocuments: React.FC = () => {
  const permissions = usePermissions();
  const [documents, setDocuments] = useState<Document[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [typeFilter, setTypeFilter] = useState<string>('all');
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);

  useEffect(() => {
    loadDocuments();
  }, [page, statusFilter, typeFilter, searchTerm]);

  const loadDocuments = async () => {
    try {
      setLoading(true);
      const params: any = {
        tenant_id: permissions.tenantId || undefined,
        page,
        size: 20
      };

      if (statusFilter !== 'all') {
        params.status = statusFilter;
      }

      if (typeFilter !== 'all') {
        params.document_type = typeFilter;
      }

      const response = await documentsApi.getAll(params);
      let filteredDocs = response.data.documents;

      // Client-side search filter
      if (searchTerm) {
        filteredDocs = filteredDocs.filter(doc =>
          doc.document_number.toLowerCase().includes(searchTerm.toLowerCase()) ||
          doc.series.toLowerCase().includes(searchTerm.toLowerCase())
        );
      }

      setDocuments(filteredDocs);
      setTotalPages(Math.ceil(response.data.total / 20));
    } catch (error) {
      console.error('Error loading documents:', error);
    } finally {
      setLoading(false);
    }
  };

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'accepted':
        return <Badge variant="default" className="bg-green-100 text-green-800">✅ Aceptado</Badge>;
      case 'pending':
        return <Badge variant="secondary">⏳ Pendiente</Badge>;
      case 'rejected':
        return <Badge variant="destructive">❌ Rechazado</Badge>;
      case 'sent':
        return <Badge variant="outline">📤 Enviado</Badge>;
      default:
        return <Badge variant="secondary">{status}</Badge>;
    }
  };

  const getDocumentTypeName = (type: string) => {
    switch (type) {
      case '01': return 'Factura';
      case '03': return 'Boleta';
      case '07': return 'Nota de Crédito';
      case '08': return 'Nota de Débito';
      default: return type;
    }
  };

  const handleDownloadXml = async (docId: string) => {
    try {
      const response = await documentsApi.getXml(docId);
      // Create blob and download
      const blob = new Blob([response.data], { type: 'application/xml' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `documento_${docId}.xml`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error downloading XML:', error);
    }
  };

  const handleResendDocument = async (docId: string) => {
    try {
      // Implement resend logic here
      console.log('Resending document:', docId);
      // await sunatApi.resendDocument(docId);
      loadDocuments(); // Reload documents after resend
    } catch (error) {
      console.error('Error resending document:', error);
    }
  };

  if (loading && documents.length === 0) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-lg">Cargando mis documentos...</div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold">Mis Documentos</h1>
          <p className="text-muted-foreground">Gestiona y revisa tus documentos electrónicos</p>
        </div>
        <Button className="flex items-center space-x-2">
          <Plus className="h-4 w-4" />
          <span>Crear Documento</span>
        </Button>
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="pt-6">
          <div className="flex flex-col md:flex-row gap-4">
            <div className="flex-1">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder="Buscar por número de documento o serie..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10"
                />
              </div>
            </div>
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-[180px]">
                <SelectValue placeholder="Estado" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todos los estados</SelectItem>
                <SelectItem value="pending">Pendiente</SelectItem>
                <SelectItem value="sent">Enviado</SelectItem>
                <SelectItem value="accepted">Aceptado</SelectItem>
                <SelectItem value="rejected">Rechazado</SelectItem>
              </SelectContent>
            </Select>
            <Select value={typeFilter} onValueChange={setTypeFilter}>
              <SelectTrigger className="w-[180px]">
                <SelectValue placeholder="Tipo" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todos los tipos</SelectItem>
                <SelectItem value="01">Facturas</SelectItem>
                <SelectItem value="03">Boletas</SelectItem>
                <SelectItem value="07">Notas de Crédito</SelectItem>
                <SelectItem value="08">Notas de Débito</SelectItem>
              </SelectContent>
            </Select>
            <Button variant="outline" onClick={loadDocuments} disabled={loading}>
              <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Documents List */}
      <Card>
        <CardHeader>
          <CardTitle>Documentos ({documents.length})</CardTitle>
        </CardHeader>
        <CardContent>
          {documents.length > 0 ? (
            <div className="space-y-4">
              {documents.map((doc) => (
                <div key={doc.id} className="border rounded-lg p-4 hover:bg-muted/50 transition-colors">
                  <div className="flex items-center justify-between">
                    <div className="flex-1">
                      <div className="flex items-center space-x-3">
                        <FileText className="h-5 w-5 text-blue-600" />
                        <div>
                          <h3 className="font-medium">
                            {getDocumentTypeName(doc.document_type)} {doc.document_number}
                          </h3>
                          <p className="text-sm text-muted-foreground">
                            Serie: {doc.series} • Fecha: {new Date(doc.issue_date).toLocaleDateString()}
                          </p>
                        </div>
                      </div>
                      <div className="mt-2 flex items-center space-x-4">
                        <span className="text-sm">
                          <strong>Monto:</strong> {doc.currency_code} {doc.total_amount}
                        </span>
                        <span className="text-sm text-muted-foreground">
                          Creado: {new Date(doc.created_at).toLocaleDateString()}
                        </span>
                      </div>
                    </div>
                    <div className="flex items-center space-x-3">
                      {getStatusBadge(doc.status)}
                      <div className="flex space-x-2">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleDownloadXml(doc.id)}
                          title="Descargar XML"
                        >
                          <Download className="h-4 w-4" />
                        </Button>
                        {(doc.status === 'rejected' || doc.status === 'pending') && (
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handleResendDocument(doc.id)}
                            title="Reenviar a SUNAT"
                          >
                            <RefreshCw className="h-4 w-4" />
                          </Button>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              ))}

              {/* Pagination */}
              {totalPages > 1 && (
                <div className="flex justify-center space-x-2 mt-6">
                  <Button
                    variant="outline"
                    disabled={page === 1}
                    onClick={() => setPage(page - 1)}
                  >
                    Anterior
                  </Button>
                  <span className="flex items-center px-4">
                    Página {page} de {totalPages}
                  </span>
                  <Button
                    variant="outline"
                    disabled={page === totalPages}
                    onClick={() => setPage(page + 1)}
                  >
                    Siguiente
                  </Button>
                </div>
              )}
            </div>
          ) : (
            <div className="text-center py-12">
              <FileText className="mx-auto h-16 w-16 text-muted-foreground" />
              <h3 className="mt-4 text-lg font-medium">No hay documentos</h3>
              <p className="mt-2 text-muted-foreground">
                {searchTerm || statusFilter !== 'all' || typeFilter !== 'all'
                  ? 'No se encontraron documentos con los filtros aplicados'
                  : 'Aún no has creado ningún documento electrónico'
                }
              </p>
              {!searchTerm && statusFilter === 'all' && typeFilter === 'all' && (
                <Button className="mt-4">
                  <Plus className="h-4 w-4 mr-2" />
                  Crear Primer Documento
                </Button>
              )}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default MyDocuments;